<template>
    <div
        class="app--engagement-group bg--group border--group"
        :data-id="definition.id"
        v-show="visible"
        :class="{ visible: visible }"
    >
        <div v-if="definition.heading" class="group-heading">
            <span
                class="title"
                :class="theme('title')"
                :title="macro(definition.title)"
                v-html="macro(definition.title)"
            ></span>
            <div class="d-flex flex-row">
                <span
                    class="description"
                    :class="theme('description')"
                    :title="macro(definition.description)"
                    v-html="macro(definition.description)"
                ></span>
                <div v-if="groupVisibleOnlyInPreview" class="app--engagement-advisor-preview-icon ms-2">
                    <app-icon :title="trans('visibility.group.preview')" value="eye" type="fas" class="fa-sm" />
                </div>
            </div>
        </div>
        <div
            v-for="(group, index) in groups"
            class="group-content"
            :class="{ active: isActive(group.id), multiple: multiple, 'with-heading': definition.heading }"
            :data-group="group.id"
        >
            <div v-if="multiple" class="group-row" @click="toggle(group)" :class="theme('rows')">
                <app-icon v-if="!isActive(group.id)" value="next" key="expand"></app-icon>
                <app-icon v-if="isActive(group.id)" value="down" key="collapse"></app-icon>
                <span class="badge">{{ badge(index, group) }}</span>
                <span class="title" v-html="title(group)"></span>
                <app-button
                    v-if="removable(group)"
                    class="danger only-icon"
                    @click.native.stop="remove(group)"
                    :disabled="loading || working"
                    icon="close"
                ></app-button>
            </div>
            <div class="group-layout" :class="{ float: definition.float }">
                <div v-for="(row, rI) in layout" class="row" :data-row="rI">
                    <div v-for="(col, cI) in row" :key="rI + ':' + cI" :class="'col-sm-' + _size(col)">
                        <div class="group-cell">
                            <engagement-element
                                v-if="col.element"
                                :panel="panel"
                                :group="group"
                                :definition="col.element"
                            ></engagement-element>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="group-footer" v-if="multiple && groups.length < limit">
            <app-button
                class="primary text-uppercase border--button"
                @click.native="add"
                :loading="loading"
                :disabled="loading || working"
                icon="add"
                :label="trans('buttons.add_more')"
            ></app-button>
        </div>
    </div>
</template>

<script>
    import { v4 as uuid } from 'uuid'
    import store from '../store'
    import Element from './Element'

    export default {
        props: {
            definition: { type: Object, required: true },
            panel: { type: Object, required: true }
        },
        data() {
            return { loading: false }
        },
        mounted() {
            store.commit('ADD_ACTIVE_GROUP', {
                panelId: this.panel.id,
                definitionId: this.definition.id,
                groupId: this.groups[0].id
            })
        },
        computed: {
            groups() {
                return store.getters['dataGroups'](this.panel, this.definition)
            },
            visible() {
                return store.getters['visibleGroup'](this.panel, this.definition)
            },
            multiple() {
                return this.definition && this.definition.type == 'MULTIPLE'
            },
            limit() {
                return this.definition && this.definition.limit ? this.definition.limit : 30
            },
            layout() {
                let layout = {}
                if (this.definition && this.definition.layout) {
                    this.definition.layout.forEach((row, rowIndex) => {
                        row.forEach((col, colIndex) => {
                            let el = col.key ? store.state.template.elements.find(e => e.id == col.key) : null
                            let size = 12

                            if (col.size == 'small') {
                                size = 3
                            }
                            if (col.size == 'medium') {
                                size = 6
                            }
                            if (col.size == 'large') {
                                size = 12
                            }
                            if (col.size) {
                                size = col.size
                            }
                            if (!layout.hasOwnProperty(rowIndex)) {
                                layout[rowIndex] = {}
                            }
                            layout[rowIndex][colIndex] = { size: size, element: el }
                        })
                    })
                }
                return layout
            },
            elements() {
                let result = []
                Object.keys(this.layout).forEach(row => {
                    Object.keys(this.layout[row]).forEach(col => {
                        if (this.layout[row][col].element) {
                            result.push(this.layout[row][col].element.id)
                        }
                    })
                })
                return result
            },
            working() {
                return store.state.loading
            },
            complete() {
                return store.state.complete
            },
            page() {
                return store.state.page
            },
            groupVisibleOnlyInPreview() {
                return (
                    this.definition.print_visibility == 'preview' &&
                    store.state.preview == true &&
                    store.state.complete == false
                )
            },
            active() {
                return store.state.active_groups.filter(item => item.definitionId === this.definition.id)
            }
        },
        methods: {
            _size(col) {
                if (col && col.size == 'small') {
                    return 3
                }
                if (col && col.size == 'medium') {
                    return 6
                }
                if (col && col.size == 'large') {
                    return 12
                }
                if (col && col.size) {
                    return col.size
                }
                return 12
            },
            trans(key) {
                return store.getters['trans'](key) || this.$t('front.' + key)
            },
            theme(key) {
                return [
                    'text--color-' + store.state.theme.groups[key].color,
                    'text--font-' + store.state.theme.groups[key].font,
                    'text--size-' + store.state.theme.groups[key].size,
                    'print--color-' + store.state.theme.print.groups[key].color,
                    'print--size-' + store.state.theme.print.groups[key].size
                ]
            },
            macro(value) {
                return store.getters['macro'](value)
            },
            removable(group) {
                if (this.groups.length <= 1) {
                    return false
                }

                return store.state.template.elements.find(e => {
                    if (
                        this.elements.indexOf(e.id) !== -1 &&
                        e.options &&
                        e.options.hasOwnProperty('readonly') &&
                        e.options.readonly == true
                    ) {
                        let el = store.getters['dataElement'](this.panel, group, e)
                        if (el && el.value) {
                            return false
                        }
                    }
                    return true
                })
            },
            badge(index, group) {
                if (index == 0) {
                    return store.state.elements.filter(e => {
                        return (
                            e.group == group.id &&
                            this.elements.indexOf(e.definition) !== -1 &&
                            (e.value != null || e.value != undefined)
                        )
                    }).length > 0
                        ? 1
                        : 0
                }
                return index + 1
            },
            title(group) {
                if (!this.definition.row) {
                    return this.macro(this.definition.title)
                }
                let element = store.getters['dataElement'](this.panel, group, { id: this.definition.row })
                if (!element) {
                    return this.macro(this.definition.title)
                }
                let formatted = store.getters['formatted'](element)

                return formatted ? formatted : this.macro(this.definition.title)
            },
            isActive(groupId) {
                return this.active.some(
                    item =>
                        item.panelId === this.panel.id &&
                        item.definitionId === this.definition.id &&
                        item.groupId === groupId
                )
            },
            toggle(group) {
                const payload = { panelId: this.panel.id, definitionId: this.definition.id, groupId: group.id }
                if (this.isActive(group.id)) {
                    store.commit('REMOVE_ACTIVE_GROUP', payload)
                } else {
                    store.commit('ADD_ACTIVE_GROUP', payload)
                }
            },
            add() {
                if (!this.loading && !this.complete) {
                    this.loading = true
                    let group = { id: uuid() }
                    store.commit('ADD_GROUP', group)
                    this.elements.forEach(id => {
                        let elid = uuid()
                        let value = store.getters['defaultValue'](id)
                        store.commit('ADD_ELEMENT', {
                            id: elid,
                            panel: this.panel.id,
                            group: group.id,
                            definition: id,
                            value: value,
                            exists: false
                        })
                        if (value) {
                            let gk = group.id + '|' + this.definition.id
                            store.commit('QUEUE', { id: elid, group: gk, value: value, default: true })
                        }
                    })
                    store.commit('ADD_ACTIVE_GROUP', {
                        panelId: this.panel.id,
                        definitionId: this.definition.id,
                        groupId: group.id
                    })
                    this.$nextTick(() => {
                        this.loading = false
                    })
                }
            },
            remove(group) {
                if (!this.loading && !this.complete) {
                    this.loading = true
                    store.state.elements
                        .filter(e => e.group == group.id && this.elements.indexOf(e.definition) !== -1)
                        .forEach(e => {
                            store.commit('QUEUE', { id: e.id, group: e.group, value: null, remove: true })
                        })
                    this.$nextTick(() => {
                        this.loading = false
                    })
                }
            }
        },
        components: {
            'engagement-element': Element
        }
    }
</script>
